import React, { Suspense, useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'

/**
 * HtmlOverlay - Fullscreen overlay for displaying HTML content in an iframe.
 */
export function HtmlOverlay({ content, showPopup, setShowPopup })
{
  const iframeRef = useRef(null)
  const containerRef = useRef(null)

  // Handle close action
  const handleClose = useCallback(() =>
  {
    setShowPopup(false)
  }, [ setShowPopup ])

  // Handle escape key to close
  useEffect(() =>
  {
    const handleKeyDown = (event) =>
    {
      if (event.key === 'Escape' && showPopup)
      {
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [ showPopup, handleClose ])

  // Don't render if not showing
  if (!showPopup)
  {
    return null
  }

  return createPortal(
    <div
      ref={containerRef}
      id="html-overlay-root"
      className="fixed inset-0 w-screen h-screen z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-label="Content popup"
    >
      {/* Full-screen glass blur backdrop - clickable to close */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[20px] cursor-pointer"
        onClick={handleClose}
      />

      {/* Centered content container */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vh] max-w-[1200px] max-h-[800px] flex flex-col">
        {/* Content wrapper - centered rectangle */}
        <div className="relative w-full h-full bg-black rounded-3xl border-2 border-white/30 shadow-2xl overflow-hidden">
          {/* Close button - rounded circle */}
          <button
            className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center text-white bg-black/80 backdrop-blur-sm border-2 border-white/30 rounded-full cursor-pointer z-20 transition-all duration-200 hover:bg-white/20 hover:border-white/50"
            onClick={handleClose}
            aria-label="Close popup"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          {/* Iframe - full size within the content area */}
          <Suspense
            fallback={
              <div className="w-full h-full flex items-center justify-center text-white text-xl bg-black">
                Loading...
              </div>
            }
          >
            {content && typeof content === 'string' && content.trim().startsWith('<') ? (
              <div
                className="w-full h-full overflow-auto"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <iframe
                ref={iframeRef}
                src={content}
                className="w-full h-full border-none block"
                title="Popup content"
              />
            )}
          </Suspense>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default HtmlOverlay
